/**
 * Native app bootstrap for Capacitor (iOS first).
 *
 * Runs once at startup. On the web this is a no-op, so the same bundle
 * serves both the website and the native app.
 */
import { Capacitor } from '@capacitor/core';

export const isNativeApp = Capacitor.isNativePlatform();
export const isIOS = Capacitor.getPlatform() === 'ios';

export async function initNativeApp(): Promise<void> {
  if (!isNativeApp) return;

  // Lets CSS target the native shell: html.native-app / html.native-ios
  document.documentElement.classList.add('native-app');
  if (isIOS) document.documentElement.classList.add('native-ios');

  const [{ StatusBar, Style }, { SplashScreen }, { Keyboard }, { App }] = await Promise.all([
    import('@capacitor/status-bar'),
    import('@capacitor/splash-screen'),
    import('@capacitor/keyboard'),
    import('@capacitor/app'),
  ]);

  // Dark app chrome: light status-bar text over the app's #09090b background.
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch {
    /* plugin unavailable (e.g. simulator quirk) — never block startup */
  }

  // Flag keyboard visibility so CSS can hide the bottom tab bar while typing.
  Keyboard.addListener('keyboardWillShow', () => {
    document.documentElement.classList.add('keyboard-open');
  });
  Keyboard.addListener('keyboardWillHide', () => {
    document.documentElement.classList.remove('keyboard-open');
  });

  // Deep links (universal links / custom scheme) → route inside the SPA.
  App.addListener('appUrlOpen', ({ url }) => {
    try {
      const path = new URL(url).pathname + new URL(url).search;
      window.history.pushState(null, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch {
      /* malformed URL — ignore */
    }
  });

  // Android hardware back (harmless on iOS): go back in history or background the app.
  App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) window.history.back();
    else App.minimizeApp();
  });

  await SplashScreen.hide();
}

/** Light impact haptic for tab taps; silently no-ops on web. */
export async function hapticTap(): Promise<void> {
  if (!isNativeApp) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch {
    /* ignore */
  }
}
